import React, { useEffect, useState } from "react";
import CommonForm from "../../../../../../components/commonform/commonForm";
import Snackbar from "../../../../../../components/snackbar/snackbar";
import moment from "moment";
import { institution_data } from "../../../../user.api";
import {
  getInternshipOpportunities,
  getInternshipQualifications,
} from "../../../../../public/public.api";
import View from "./viewDetails";

let flag = false;
let eighteenYearsAgo = new Date();
eighteenYearsAgo = eighteenYearsAgo.setFullYear(
  eighteenYearsAgo.getFullYear() - 18,
);

// ─── Step indicator ────────────────────────────────────────────────────────────
const STEPS = ["Basic Details", "Education", "Internships", "Preview"];

function StepIndicator({ current }) {
  return (
    <div className="msf-step-indicator">
      {STEPS.map((label, idx) => {
        const done = idx < current;
        const active = idx === current;
        return (
          <React.Fragment key={idx}>
            <div
              className={`msf-step ${active ? "msf-step--active" : ""} ${done ? "msf-step--done" : ""}`}
            >
              <div className="msf-step__circle">
                {done ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M2 7l4 4 6-6"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>
              <div className="msf-step__label">{label}</div>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`msf-step__connector ${done ? "msf-step__connector--done" : ""}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function AreaDetails(props) {
  const [formData, setFormData] = useState(props.data);
  const [count, setCount] = useState(1);
  const [institutions, setInstitutions] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState("");
  const [institutionError, setInstitutionError] = useState("");
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [keyvalue, setKeyValue] = useState(1);
  const [snackOpen, setSnackOpen] = useState(false);
  const [err, setErr] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const [isKeralaNative, setIsKeralaNative] = useState(
    props.data?.is_kerala_native ?? null,
  );
  const [courseCompletionStatus, setCourseCompletionStatus] = useState(
    props.data?.course_completion_status || "",
  );
  const [qualificationsData, setQualificationsData] = useState([]);
  const [internshipOpportunities, setInternshipOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [selectedProposals, setSelectedProposals] = useState([]);

  // formPage: 0=BasicDetails, 1=Education, 2=Internships, 3=Preview
  const [formPage, setFormPage] = useState(props.initialPage || 0);

  // ── Init default data ────────────────────────────────
  useEffect(() => {
    if (!formData.qualifications) {
      setFormData((prev) => ({
        ...prev,
        qualifications: [
          {
            qualification_type_id: "",
            qualification_id: "",
            stream_id: "",
            mark_system: "",
            board: "",
            year_passed: "",
            marks: [],
            certificate_photo: null,
          },
        ],
      }));
    }
    if (!formData.otherLanguages) {
      setFormData((prev) => ({ ...prev, otherLanguages: [{ language: "" }] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch data (initial — no filters) ───────────────
  useEffect(() => {
    (async () => {
      try {
        const [qRes] = await Promise.all([getInternshipQualifications()]);
        const qData = Array.isArray(qRes?.data)
          ? qRes.data
          : qRes?.data?.data || [];
        setQualificationsData(qData);
        if (props.onDataLoaded) props.onDataLoaded(qData, []);
      } catch (e) {
        console.error("Error fetching qualifications:", e);
      }
    })();
    institution_data()
      .then((res) => setInstitutions(res?.data))
      .catch((e) => console.error("Error fetching institutions:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch filtered internship opportunities ───────────
  const fetchFilteredOpportunities = async (showAll = false) => {
    setOpportunitiesLoading(true);
    try {
      // Map course completion status to API target_category value
      const targetCategory =
        courseCompletionStatus === "completed"
          ? "students_completed_course"
          : courseCompletionStatus === "undergoing"
            ? "students_undergoing_course"
            : undefined;

      // Use the first qualification's type and parent (qualification_id acts as parent)
      const firstQual = (formData.qualifications || [])[0] || {};

      const params = showAll
        ? undefined
        : {
          ...(firstQual.qualification_type_id && {
            qualification_type: firstQual.qualification_type_id,
          }),
          ...(firstQual.qualification_id && {
            qualification_parent: firstQual.qualification_id,
          }),
          ...(targetCategory && { target_category: targetCategory }),
        };

      const res = await getInternshipOpportunities(params);
      const data = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
      setInternshipOpportunities(data);
      if (props.onDataLoaded) props.onDataLoaded(qualificationsData, data);
    } catch (e) {
      console.error("Error fetching opportunities:", e);
      showError("Failed to load internship opportunities");
    } finally {
      setOpportunitiesLoading(false);
    }
  };

  // ── Snackbar helper ───────────────────────────────────
  const showError = (msg, redirect = false) => {
    setSnackMessage(msg);
    setIsSnackError(true);
    setSnackOpen(true);
    setShouldRedirect(redirect);
  };
  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
    if (shouldRedirect) {
      window.location.href = "/";
    }
  };

  // ── Field handlers ────────────────────────────────────
  const textFieldChange = (e) => {
    let { name, value } = e.target;
    if (name === "full_name") {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setKeyValue((k) => k + 1);
  };
  const genderFieldChange = (e) =>
    setFormData((prev) => ({ ...prev, gender: e.target.value }));
  const handleOtherLangChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.checked }));

  const handleDatePickerChange = (e, name) => {
    const date = e.value;
    setErr(!/^[0-9]{2}-[0-9]{2}-[0-9]{4}$/.test(date));
    setFormData((prev) => ({ ...prev, [name]: date }));
    setKeyValue((k) => k + 1);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = file.name.split(".").pop().toLowerCase();
    if (["png", "jpeg", "jpg"].includes(type)) {
      if (file.size / 1024 / 1024 < 1) {
        setFormData((prev) => ({ ...prev, file_name: file.name, photo: file }));
      } else {
        showError("File size is greater than 1MB");
        e.target.value = "";
        setFormData((prev) => ({ ...prev, file_name: null, photo: null }));
      }
    } else {
      showError("File should be in .jpg / .jpeg / .png format");
      e.target.value = "";
      setFormData((prev) => ({ ...prev, file_name: null, photo: null }));
    }
  };

  const handleResumeFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = file.name.split(".").pop().toLowerCase();
    if (type === "pdf") {
      if (file.size / 1024 / 1024 < 5) {
        setFormData((prev) => ({
          ...prev,
          resume_name: file.name,
          resume: file,
        }));
      } else {
        showError("File size is greater than 5MB");
        e.target.value = "";
        setFormData((prev) => ({ ...prev, resume_name: null, resume: null }));
      }
    } else {
      showError("File should be in .pdf format");
      e.target.value = "";
      setFormData((prev) => ({ ...prev, resume_name: null, resume: null }));
    }
  };

  const handleRecFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = file.name.split(".").pop().toLowerCase();
    if (type === "pdf") {
      if (file.size / 1024 / 1024 < 5) {
        setFormData((prev) => ({
          ...prev,
          rec_letter_name: file.name,
          rec_letter: file,
        }));
      } else {
        showError("File size is greater than 5MB");
        e.target.value = "";
        setFormData((prev) => ({
          ...prev,
          rec_letter_name: null,
          rec_letter: null,
        }));
      }
    } else {
      showError("File should be in .pdf format");
      e.target.value = "";
      setFormData((prev) => ({
        ...prev,
        rec_letter_name: null,
        rec_letter: null,
      }));
    }
  };

  const handleCategoryChange = (e) => {
    const id = e.target.value;
    if (id === "others") {
      setSelectedCatId("others");
      setFormData((prev) => ({
        ...prev,
        institution_id: "others",
        institution_name: "Others",
      }));
      showError(
        "Academic institution is not registered with us, please contact your institution head.",
        true,
      );
      return;
    }
    setInstitutionError("");
    setSelectedCatId(id);
    const inst = institutions.find((i) => i.id.toString() === id);
    setFormData((prev) => ({
      ...prev,
      institution_id: id,
      institution_name: inst?.institution_name || "",
    }));
  };

  // ── Language handlers ─────────────────────────────────
  const handleValid = (type) => {
    if (type === "language")
      return (props.languageData || []).some((l) => l.checked);
    return false;
  };
  const handleCheckChange = (e, type, item) => {
    if (type === "language") {
      const data = [...props.languageData];
      data[item]["checked"] = e.target.checked;
      props.setLanguageData(data);
    }
  };
  const handleMultiChange = (e, i) => {
    setFormData((prev) => {
      const langs = [...prev.otherLanguages];
      langs[i] = { ...langs[i], [e.target.name]: e.target.value };
      return { ...prev, otherLanguages: langs };
    });
  };
  const addLanguage = () =>
    setFormData((prev) => ({
      ...prev,
      otherLanguages: [...prev.otherLanguages, { language: "" }],
    }));
  const removeLanguage = (i) => {
    setFormData((prev) => {
      const langs = [...prev.otherLanguages];
      langs.splice(i, 1);
      return { ...prev, otherLanguages: langs };
    });
  };

  // ── Educational handlers ──────────────────────────────
  const handleEducationalChange = (e, i) => {
    setFormData((prev) => {
      const quals = [...prev.qualifications];
      quals[i] = { ...quals[i], [e.target.name]: e.target.value };
      if (e.target.name === "qualification_type_id") {
        quals[i].qualification_id = "";
        quals[i].stream_id = "";
      }
      if (e.target.name === "qualification_id") quals[i].stream_id = "";
      if (e.target.name === "mark_system") quals[i].marks = [];
      return { ...prev, qualifications: quals };
    });
  };
  const handleEducationalNumberChange = (e, i) => {
    let value = e.target.value
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1");
    if (e.target.name === "year_passed") {
      value = value.substring(0, 4);
    }
    setFormData((prev) => {
      const quals = [...prev.qualifications];
      quals[i] = { ...quals[i], [e.target.name]: value };
      return { ...prev, qualifications: quals };
    });
  };
  const handleEducationalFileChange = (e, i) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = file.name.split(".").pop().toLowerCase();
    if (type === "pdf") {
      if (file.size / 1024 / 1024 < 1) {
        setFormData((prev) => {
          const quals = [...prev.qualifications];
          quals[i] = {
            ...quals[i],
            file_name: file.name,
            certificate_photo: file,
          };
          return { ...prev, qualifications: quals };
        });
      } else {
        showError("File size is greater than 1MB");
        e.target.value = "";
        setFormData((prev) => {
          const quals = [...prev.qualifications];
          quals[i] = { ...quals[i], file_name: null, certificate_photo: null };
          return { ...prev, qualifications: quals };
        });
      }
    } else {
      showError("File should be in .pdf format");
      e.target.value = "";
      setFormData((prev) => {
        const quals = [...prev.qualifications];
        quals[i] = { ...quals[i], file_name: null, certificate_photo: null };
        return { ...prev, qualifications: quals };
      });
    }
  };
  const addeducationalQualifications = () => {
    setFormData((prev) => ({
      ...prev,
      qualifications: [
        ...prev.qualifications,
        {
          qualification_type_id: "",
          qualification_id: "",
          stream_id: "",
          mark_system: "",
          board: "",
          year_passed: "",
          marks: [],
          certificate_photo: null,
        },
      ],
    }));
  };
  const removeducationalQualifications = (i) => {
    setFormData((prev) => {
      const quals = [...prev.qualifications];
      quals.splice(i, 1);
      return { ...prev, qualifications: quals };
    });
  };

  const handleMarksChange = (qIndex, mIndex, field, value) => {
    let nextValue = value;

    if (field === "cgpa") {
      nextValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

      if (nextValue.includes(".")) {
        const [wholePart, decimalPart] = nextValue.split(".");
        nextValue = `${wholePart}.${(decimalPart || "").slice(0, 2)}`;
      }
    }

    setFormData((prev) => {
      const quals = [...prev.qualifications];
      const marks = [...(quals[qIndex]?.marks || [])];
      marks[mIndex] = { ...marks[mIndex], [field]: nextValue };
      quals[qIndex] = { ...quals[qIndex], marks };
      return { ...prev, qualifications: quals };
    });
  };
  const addMarkRow = (qIndex) => {
    setFormData((prev) => {
      const quals = [...prev.qualifications];
      const q = quals[qIndex];
      const isSemester = q.mark_system === "semester_gpa";
      const next = (q.marks || []).length + 1;
      const newRow = isSemester
        ? {
          period_type: "semester",
          period_number: next,
          period_label: `Semester ${next}`,
          cgpa: "",
        }
        : {
          period_type: "year",
          period_number: next,
          period_label: `Year ${next}`,
          marks_obtained: "",
          total_marks: "",
        };
      quals[qIndex] = { ...q, marks: [...(q.marks || []), newRow] };
      return { ...prev, qualifications: quals };
    });
  };
  const removeMarkRow = (qIndex, mIndex) => {
    setFormData((prev) => {
      const quals = [...prev.qualifications];
      const q = quals[qIndex];
      const marks = [...(q.marks || [])];
      marks.splice(mIndex, 1);
      const isSem = q.mark_system === "semester_gpa";
      quals[qIndex] = {
        ...q,
        marks: marks.map((m, idx) => ({
          ...m,
          period_type: isSem ? "semester" : "year",
          period_number: idx + 1,
          period_label: isSem ? `Semester ${idx + 1}` : `Year ${idx + 1}`,
        })),
      };
      return { ...prev, qualifications: quals };
    });
  };

  // ── Qualification lookup helpers ──────────────────────
  const getQualificationTypes = () =>
    (qualificationsData || []).filter((q) => q.parent_id === null);
  const getQualificationsByType = (typeId) => {
    const type = (qualificationsData || []).find(
      (q) => q.id?.toString() === typeId?.toString(),
    );
    return type?.children_recursive || [];
  };
  const getStreamsByQualification = (typeId, qualId) => {
    const qual = getQualificationsByType(typeId).find(
      (q) => q.id?.toString() === qualId?.toString(),
    );
    return qual?.children_recursive || [];
  };

  // ── Proposal selection ────────────────────────────────
  const handleProposalSelection = (op) => {
    const exists = selectedProposals.some(
      (p) => p.internship_request_id === op.id,
    );
    if (exists) {
      setSelectedProposals(
        selectedProposals
          .filter((p) => p.internship_request_id !== op.id)
          .map((p, idx) => ({ ...p, preference_order: idx + 1 })),
      );
      return;
    }
    if (selectedProposals.length >= 5) {
      showError("You can select upto 5 opportunities");
      return;
    }
    setSelectedProposals([
      ...selectedProposals,
      {
        internship_request_id: op.id,
        preference_order: selectedProposals.length + 1,
      },
    ]);
  };

  // ── Page navigation with validation ──────────────────
  const validatePage0 = () => {
    // Check all mandatory fields for a generic error message
    const isBasicMissing =
      isKeralaNative === null ||
      !formData?.institution_id ||
      formData?.institution_id === "others" ||
      !formData?.full_name ||
      !formData?.email ||
      !formData?.phone_no ||
      !formData?.date_of_birth ||
      !formData?.gender ||
      (!formData?.photo && !formData?.file_name) ||
      (!formData?.rec_letter && !formData?.rec_letter_name) ||
      (!formData?.resume && !formData?.resume_name) ||
      !formData?.address;

    const hasStandardLang = (props.languageData || []).some((l) => l.checked);
    const hasOtherLangInput =
      formData.any_other_lang &&
      formData.otherLanguages?.some((l) => l.language.trim());
    const isLangMissing = !hasStandardLang && !hasOtherLangInput;

    if (isBasicMissing || isLangMissing) {
      showError("Please fill all mandatory fields");
      return false;
    }

    if (isKeralaNative === false) {
      showError("This registration is for Kerala natives only. You are not eligible to proceed with this application.", true);
      return false;
    }
    if (!formData?.institution_id || formData?.institution_id === "others") {
      showError(
        "Academic institution is not registered with us, please contact your institution head.",
        true,
      );
      return false;
    }

    if (
      formData.any_other_lang &&
      !formData.otherLanguages?.some((l) => l.language.trim())
    ) {
      showError("Please enter at least one other language");
      return false;
    }

    const maxDate = new Date(eighteenYearsAgo);
    if (
      moment(formData.date_of_birth, "DD-MM-YYYY") >
      moment(maxDate, "DD-MM-YYYY")
    ) {
      showError("Please choose a valid Date of Birth (must be 18+)");
      return false;
    }
    return true;
  };

  const validatePage1 = () => {
    let valid = true;
    
    if (!courseCompletionStatus) {
      showError("Please select Educational Qualification Status");
      valid = false;
    }

    (formData.qualifications || []).forEach((q, i) => {
      if (
        !q.qualification_type_id ||
        !q.qualification_id ||
        !q.mark_system ||
        !q.board ||
        !q.year_passed
      ) {
        showError(
          `Please complete all required fields for Qualification #${i + 1}`,
        );
        valid = false;
      }
      if (
        q.year_passed &&
        (q.year_passed.length !== 4 ||
          isNaN(q.year_passed) ||
          parseInt(q.year_passed) < 1900 ||
          parseInt(q.year_passed) > 2035)
      ) {
        showError(
          `Please enter a valid 4-digit Year of Passing for Qualification #${i + 1}`,
        );
        valid = false;
      }
      if (!q.certificate_photo && !q.file_name) {
        showError(`Please upload certificate for Qualification #${i + 1}`);
        valid = false;
      }

      if (!q.marks || q.marks.length === 0) {
        showError(
          `Please add at least one mark/CGPA row for Qualification #${i + 1}`,
        );
        valid = false;
      } else {
        q.marks.forEach((m, mIdx) => {
          if (q.mark_system === "semester_gpa") {
            if (!m.cgpa || m.cgpa === "" || isNaN(m.cgpa)) {
              showError(
                `Please enter a valid CGPA for ${m.period_label} in Qualification #${i + 1}`,
              );
              valid = false;
            } else if (parseFloat(m.cgpa) > 10) {
              showError(
                `CGPA cannot be greater than 10 in ${m.period_label} for Qualification #${i + 1}`,
              );
              valid = false;
            }
          } else {
            if (
              !m.marks_obtained ||
              m.marks_obtained === "" ||
              !m.total_marks ||
              m.total_marks === "" ||
              isNaN(m.marks_obtained) ||
              isNaN(m.total_marks)
            ) {
              showError(
                `Please enter valid marks for ${m.period_label} in Qualification #${i + 1}`,
              );
              valid = false;
            } else if (
              parseFloat(m.marks_obtained) > parseFloat(m.total_marks)
            ) {
              showError(
                `Marks obtained cannot be greater than total marks in ${m.period_label} for Qualification #${i + 1}`,
              );
              valid = false;
            }
          }
        });
      }
    });
    return valid;
  };

  const validatePage2 = () => {
    if (selectedProposals.length === 0) {
      showError("Please select at least one internship opportunity");
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (formPage === 0 && !validatePage0()) return;
    if (formPage === 1 && !validatePage1()) return;
    if (formPage === 2 && !validatePage2()) return;

    const nextPage = formPage + 1;
    setFormPage(nextPage);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      document
        .querySelector(".expert")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);

    // When entering the internship selection page, fetch with filters
    if (nextPage === 2) {
      fetchFilteredOpportunities(false);
    }
  };

  const goPrev = () => {
    setFormPage((p) => p - 1);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      document
        .querySelector(".expert")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  // ── Final submit (from preview page) ─────────────────
  const handleFinalSubmit = () => {
    const languages = (props.languageData || [])
      .filter((l) => l.checked)
      .map((l) => ({ language: l.language }))
      .concat(
        (formData.otherLanguages || [])
          .filter((l) => l.language)
          .map((l) => ({ language: l.language })),
      );

    const finalPayload = {
      ...formData,
      is_kerala_native: true,
      course_completion_status: courseCompletionStatus,
      languages,
      proposals: selectedProposals,
    };
    props.action(2, finalPayload);
  };

  // ── Render ────────────────────────────────────────────
  return (
    <div className="expert">
      <StepIndicator current={formPage} />
      <small className="mand">* All fields are Mandatory</small>

      {/* ══════════════ PAGE 0 — BASIC DETAILS ══════════════ */}
      {formPage === 0 && (
        <div className="msf-page">
          <h5 className="msf-page-title">Basic Details</h5>

          {/* Kerala Native */}
          <div className="msf-section">
            <div className="header-label">
              Are you a native of Kerala? <span className="text-danger">*</span>
            </div>
            <div className="d-flex gap-3">
              <button
                type="button"
                className={`msf-choice-btn ${isKeralaNative === true ? "msf-choice-btn--active" : ""}`}
                onClick={() => setIsKeralaNative(true)}
              >
                ✓ Yes
              </button>
              <button
                type="button"
                className={`msf-choice-btn msf-choice-btn--danger ${isKeralaNative === false ? "msf-choice-btn--danger-active" : ""}`}
                onClick={() => {
                  setIsKeralaNative(false);
                  showError("This registration is for Kerala natives only. You are not eligible to proceed with this application.", true);
                }}
              >
                ✗ No
              </button>
            </div>
          </div>

          {/* Course Completion */}
          {/* <div className="msf-section">
            <div className="header-label">
              Course Completion Status <span className="text-danger">*</span>
            </div>
            <div className="d-flex gap-3 flex-wrap">
              {["undergoing", "completed"].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`msf-choice-btn ${courseCompletionStatus === s ? "msf-choice-btn--active" : ""}`}
                  onClick={() => {
                    setCourseCompletionStatus(s);
                    setFormData((p) => ({ ...p, course_completion_status: s }));
                  }}
                  style={{ textTransform: "capitalize" }}
                >
                  {courseCompletionStatus === s ? "✓ " : ""}
                  {s}
                </button>
              ))}
            </div>
          </div> */}

          {/* Institution */}
          <div className="col-xs-12 col-sm-12 mb-2">
            <div className="header-label mb-1">
              Institution <span className="text-danger">*</span>
            </div>
            <select
              className="form-select selectbox mandatory-select"
              required
              name="institution_id"
              value={formData.institution_id || ""}
              onChange={handleCategoryChange}
            >
              <option value="">-- Select Institution --</option>
              {institutions?.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.institution_name}
                </option>
              ))}
              <option value="others">Others</option>
            </select>
            {institutionError && (
              <div style={{ color: "red", marginTop: 4 }}>
                {institutionError}
              </div>
            )}
          </div>

          {/* Name / Email / Phone */}
          <div className="col-xs-12 col-sm-12">
            <CommonForm
              fieldType="text"
              label="Full Name"
              required
              disabled={formData.id}
              name="full_name"
              value={formData.full_name ?? ""}
              onChange={textFieldChange}
              {...props}
            />
          </div>
          <div className="col-xs-12 col-sm-12">
            <CommonForm
              fieldType="email"
              label="Email Address"
              required
              disabled={formData.id}
              name="email"
              value={formData.email ?? ""}
              onChange={textFieldChange}
              {...props}
            />
          </div>
          <div className="col-xs-12 col-sm-12">
            <CommonForm
              fieldType="text"
              label="Phone Number"
              required
              disabled
              name="phone_no"
              value={formData.phone_no ?? ""}
              onChange={textFieldChange}
              {...props}
            />
          </div>

          {/* DOB + Gender */}
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-3">
            <div className="col-xs-12 col-sm-6">
              <CommonForm
                fieldType="date"
                label="Date of Birth"
                required
                disabled={formData.id}
                name="date_of_birth"
                maxDate={new Date(eighteenYearsAgo)}
                value={formData?.date_of_birth ?? ""}
                onChange={(e) => handleDatePickerChange(e, "date_of_birth")}
                autoComplete="off"
                {...props}
              />
              {err && (
                <div className="file-up-helper mt-1">
                  Please select a valid date
                </div>
              )}
            </div>
            <div className="col-xs-12 col-sm-5 fieldset">
              <div className="mylabel mb-2">
                Gender <span className="text-danger">*</span>
              </div>
              <div className="d-flex gap-3">
                {["Male", "Female", "Other"].map((g) => (
                  <CommonForm
                    key={g}
                    fieldType="radio"
                    label={g}
                    required
                    disabled={formData.id}
                    name="gender"
                    value={g}
                    checked={formData?.gender === g}
                    onChange={genderFieldChange}
                    {...props}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Photo */}
          <div className="col-xs-12 col-sm-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1">
                <CommonForm
                  fieldType="file"
                  required={!formData.file_name}
                  label="Photo"
                  name="photo"
                  id="file_submitted_record"
                  accept=".jpg,.jpeg,.png"
                  type="file"
                  variant="outlined"
                  fileName={formData.file_name}
                  onChange={handleFileChange}
                  {...props}
                />
              </div>
              {formData.file_upload_photo && (
                <div className="ms-2">
                  <a
                    href={formData.file_upload_photo}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-secondary btn-sm"
                    title="Download Current Photo"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </a>
                </div>
              )}
            </div>
            <div className="file-up-helper">
              *Upload only .jpg, .jpeg, .png — max 1MB
            </div>
          </div>

          {/* Recommendation Letter */}
          <div className="col-xs-12 col-sm-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1">
                <CommonForm
                  fieldType="file"
                  required={!formData.rec_letter_name}
                  label="Recommendation Letter"
                  name="rec_letter"
                  id="rec_letter"
                  accept=".pdf"
                  type="file"
                  variant="outlined"
                  fileName={formData.rec_letter_name}
                  onChange={handleRecFileChange}
                  {...props}
                />
              </div>
              {formData.rec_letter &&
                typeof formData.rec_letter === "string" && (
                  <div className="ms-2">
                    <a
                      href={
                        formData.rec_letter.startsWith("http")
                          ? formData.rec_letter
                          : `${process.env.REACT_APP_BASE_URL.replace("/api/", "")}/${formData.rec_letter}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-secondary btn-sm"
                      title="Download Current Recommendation Letter"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </a>
                  </div>
                )}
            </div>
            <div className="file-up-helper">*Upload only .pdf — max 5MB</div>
          </div>

          {/* Resume */}
          <div className="col-xs-12 col-sm-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1">
                <CommonForm
                  fieldType="file"
                  required={!formData.resume_name}
                  label="Resume"
                  name="resume"
                  id="resume"
                  accept=".pdf"
                  type="file"
                  variant="outlined"
                  fileName={formData.resume_name}
                  onChange={handleResumeFileChange}
                  {...props}
                />
              </div>
              {formData.resume && typeof formData.resume === "string" && (
                <div className="ms-2">
                  <a
                    href={
                      formData.resume.startsWith("http")
                        ? formData.resume
                        : `${process.env.REACT_APP_BASE_URL.replace("/api/", "")}/${formData.resume}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-secondary btn-sm"
                    title="Download Current Resume"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </a>
                </div>
              )}
            </div>
            <div className="file-up-helper">*Upload only .pdf — max 5MB</div>
          </div>

          {/* Address */}
          <div className="col-xs-12 col-sm-12">
            <CommonForm
              fieldType="text"
              label="Address"
              multiline
              required
              value={formData.address || ""}
              className="no-margin"
              name="address"
              onChange={textFieldChange}
              {...props}
            />
          </div>

          {/* Languages */}
          <div className="fieldset">
            <div className="header-label">
              Languages Known <span className="text-danger">*</span>
            </div>
            <div className="check-box-cont">
              {(props.languageData || []).map((item, index) => (
                <CommonForm
                  key={index}
                  fieldType="checkbox"
                  required={
                    !formData.any_other_lang && !handleValid("language")
                  }
                  value={item.checked}
                  checked={item.checked}
                  onChange={(e) => handleCheckChange(e, "language", index)}
                  label={item.language}
                  {...props}
                />
              ))}
              <CommonForm
                fieldType="checkbox"
                checked={formData.any_other_lang}
                value={formData.any_other_lang}
                name="any_other_lang"
                onChange={handleOtherLangChange}
                label="Others"
                {...props}
              />
            </div>
            {formData.any_other_lang && (
              <>
                {(formData.otherLanguages || []).map((e, i) => (
                  <React.Fragment key={i}>
                    <CommonForm
                      fieldType="text"
                      label="Language"
                      required
                      name="language"
                      value={e?.language || ""}
                      onChange={(ev) => handleMultiChange(ev, i)}
                      {...props}
                    />
                    <div className="more-btn-cnt">
                      {formData.otherLanguages.length === i + 1 && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ marginLeft: 5 }}
                          onClick={addLanguage}
                        >
                          Add More
                        </button>
                      )}
                      {formData.otherLanguages.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => removeLanguage(i)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </>
            )}
          </div>

          {/* Next button */}
          <div className="msf-nav-buttons">
            <div />
            <button
              type="button"
              className="msf-btn msf-btn--next"
              onClick={goNext}
            >
              Next: Education <span className="msf-btn-arrow">→</span>
            </button>
          </div>
        </div>
      )}

      {/* ══════════════ PAGE 1 — EDUCATIONAL QUALIFICATIONS ══════════════ */}
      {formPage === 1 && (
        <div className="msf-page">
          <h5 className="msf-page-title">Educational Qualifications</h5>

          {(formData.qualifications || []).map((e, i) => (
            <div key={i} className="msf-qual-card">
              <div className="msf-qual-card__header">
                <span className="msf-qual-card__num">#{i + 1}</span>
                <span className="msf-qual-card__title">
                  Qualification Details
                </span>
                {(formData.qualifications || []).length > 1 && (
                  <button
                    type="button"
                    className="msf-qual-card__remove"
                    onClick={() => removeducationalQualifications(i)}
                  >
                    ✕ Remove
                  </button>
                )}
              </div>
              <div className="msf-section px-4 pt-2">
                <div className="header-label">
                  Educational Qualification Status{" "}
                  <span className="text-danger">*</span>
                </div>
                <div className="d-flex gap-3 flex-wrap">
                  {["undergoing", "completed"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`msf-choice-btn ${courseCompletionStatus === s ? "msf-choice-btn--active" : ""}`}
                      onClick={() => {
                        setCourseCompletionStatus(s);
                        setFormData((p) => ({
                          ...p,
                          course_completion_status: s,
                        }));
                      }}
                      style={{ textTransform: "capitalize" }}
                    >
                      {courseCompletionStatus === s ? "✓ " : ""}
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="msf-qual-card__body">
                <div className="row">
                  <div className="col-xs-12 col-sm-6 mb-3">
                    <div className="header-label mb-1">
                      Qualification Type <span className="text-danger">*</span>
                    </div>
                    <select
                      className="form-select selectbox mandatory-select"
                      style={{ height: 45 }}
                      required
                      name="qualification_type_id"
                      value={e.qualification_type_id}
                      onChange={(ev) => handleEducationalChange(ev, i)}
                    >
                      <option value="">-- Select Type --</option>
                      {getQualificationTypes().map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-xs-12 col-sm-6 mb-3">
                    <div className="header-label mb-1">
                      Qualification <span className="text-danger">*</span>
                    </div>
                    <select
                      className="form-select selectbox mandatory-select"
                      style={{ height: 45 }}
                      required
                      name="qualification_id"
                      disabled={!e.qualification_type_id}
                      value={e.qualification_id}
                      onChange={(ev) => handleEducationalChange(ev, i)}
                    >
                      <option value="">-- Select Qualification --</option>
                      {getQualificationsByType(e.qualification_type_id).map(
                        (q) => (
                          <option key={q.id} value={q.id}>
                            {q.name}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <div className="col-xs-12 col-sm-6 mb-3">
                    <div className="header-label mb-1">Stream</div>
                    <select
                      className="form-select selectbox mandatory-select"
                      style={{ height: 45 }}
                      name="stream_id"
                      disabled={!e.qualification_id}
                      value={e.stream_id || ""}
                      onChange={(ev) => handleEducationalChange(ev, i)}
                    >
                      <option value="">-- Select Stream --</option>
                      {getStreamsByQualification(
                        e.qualification_type_id,
                        e.qualification_id,
                      ).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-xs-12 col-sm-6 mb-3">
                    <div className="header-label mb-1">
                      Mark System <span className="text-danger">*</span>
                    </div>
                    <select
                      className="form-select selectbox mandatory-select"
                      style={{ height: 45 }}
                      required
                      name="mark_system"
                      value={e.mark_system}
                      onChange={(ev) => handleEducationalChange(ev, i)}
                    >
                      <option value="">-- Select --</option>
                      <option value="semester_gpa">Semester GPA</option>
                      <option value="percentage">Percentage (Yearly)</option>
                    </select>
                  </div>
                  <div className="col-xs-12 col-sm-6 mb-3">
                    <CommonForm
                      fieldType="text"
                      label="Board/University"
                      required
                      name="board"
                      value={e.board || ""}
                      onChange={(ev) => handleEducationalChange(ev, i)}
                      {...props}
                    />
                  </div>
                  <div className="col-xs-12 col-sm-6 mb-3">
                    <CommonForm
                      fieldType="text"
                      label="Year of Passing"
                      required
                      name="year_passed"
                      value={e.year_passed || ""}
                      onChange={(ev) => handleEducationalNumberChange(ev, i)}
                      {...props}
                    />
                  </div>
                </div>

                {e.mark_system && (
                  <div className="marks-section mt-2 p-3 border rounded bg-white">
                    <h6 className="mb-3">
                      Marks / CGPA Details{" "}
                      <span className="text-danger">*</span>
                    </h6>
                    {(e.marks || []).map((m, mIdx) => (
                      <div key={mIdx} className="row align-items-end mb-2">
                        <div className="col-sm-3">
                          <label className="form-label fw-semibold">
                            {m.period_label}
                          </label>
                        </div>
                        {e.mark_system === "semester_gpa" ? (
                          <div className="col-sm-6">
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              placeholder="CGPA"
                              value={m.cgpa}
                              onWheel={(e) => e.target.blur()}
                              onChange={(ev) =>
                                handleMarksChange(
                                  i,
                                  mIdx,
                                  "cgpa",
                                  ev.target.value,
                                )
                              }
                            />
                          </div>
                        ) : (
                          <>
                            <div className="col-sm-3">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Marks Obtained"
                                value={m.marks_obtained}
                                onWheel={(e) => e.target.blur()}
                                onChange={(ev) =>
                                  handleMarksChange(
                                    i,
                                    mIdx,
                                    "marks_obtained",
                                    ev.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="col-sm-3">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Total Marks"
                                value={m.total_marks}
                                onWheel={(e) => e.target.blur()}
                                onChange={(ev) =>
                                  handleMarksChange(
                                    i,
                                    mIdx,
                                    "total_marks",
                                    ev.target.value,
                                  )
                                }
                              />
                            </div>
                          </>
                        )}
                        <div className="col-sm-3">
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => removeMarkRow(i, mIdx)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm mt-2"
                      onClick={() => addMarkRow(i)}
                    >
                      + Add{" "}
                      {e.mark_system === "semester_gpa" ? "Semester" : "Year"}
                    </button>
                  </div>
                )}

                <div className="row mt-3">
                  <div className="col-12">
                    <CommonForm
                      fieldType="file"
                      required={!e.file_name}
                      label="Upload Certificate (.pdf only, max 1MB)"
                      name="certificate_photo"
                      id={`cert_${i}`}
                      accept=".pdf"
                      fileName={e.file_name}
                      onChange={(ev) => handleEducationalFileChange(ev, i)}
                      {...props}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="msf-add-qual-btn"
            onClick={addeducationalQualifications}
          >
            <span style={{ fontSize: 20, lineHeight: 1 }}>+</span> Add Another
            Qualification
          </button>

          <div className="msf-nav-buttons">
            <button
              type="button"
              className="msf-btn msf-btn--prev"
              onClick={goPrev}
            >
              <span className="msf-btn-arrow">←</span> Previous
            </button>
            <button
              type="button"
              className="msf-btn msf-btn--next"
              onClick={goNext}
            >
              Next: Internships <span className="msf-btn-arrow">→</span>
            </button>
          </div>
        </div>
      )}

      {/* ══════════════ PAGE 2 — INTERNSHIP SELECTION ══════════════ */}
      {formPage === 2 && (
        <div className="msf-page">
          <h5 className="msf-page-title">Select Internship Opportunities</h5>
          <p className="msf-page-subtitle">
            Choose up to <strong>5</strong> opportunities in order of
            preference. Your selection order becomes your priority ranking.
          </p>

          {selectedProposals.length > 0 && (
            <div className="msf-selection-summary">
              <div className="msf-selection-summary__label">
                Selected ({selectedProposals.length}/5)
              </div>
              <div className="msf-selection-summary__chips">
                {selectedProposals.map((p, idx) => {
                  const op = internshipOpportunities.find(
                    (o) => o.id === p.internship_request_id,
                  );
                  return (
                    <span key={idx} className="msf-priority-chip">
                      <span className="msf-priority-chip__num">
                        {p.preference_order}
                      </span>
                      {op?.internship_discipline ||
                        `Opportunity ${p.internship_request_id}`}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <div className="msf-opportunity-grid">
            {opportunitiesLoading ? (
              // Skeleton loading cards
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="msf-opportunity-card msf-opportunity-card--skeleton"
                >
                  <div className="msf-skeleton-header" />
                  <div className="msf-opportunity-card__body">
                    <div
                      className="msf-skeleton-line"
                      style={{ width: "70%", marginBottom: 10 }}
                    />
                    <div
                      className="msf-skeleton-line"
                      style={{ width: "50%", marginBottom: 10 }}
                    />
                    <div
                      className="msf-skeleton-line"
                      style={{ width: "40%" }}
                    />
                  </div>
                  <div className="msf-opportunity-card__footer">
                    <div
                      className="msf-skeleton-line"
                      style={{ height: 34, borderRadius: 6 }}
                    />
                  </div>
                </div>
              ))
            ) : internshipOpportunities.length === 0 ? (
              <div className="msf-no-results">
                <div className="msf-no-results__icon">🔍</div>
                <div className="msf-no-results__title">
                  No matching opportunities found
                </div>
                <p className="msf-no-results__sub">
                  No internships match your qualification and course completion
                  criteria.
                </p>
                {/* <button
                  type="button"
                  className="msf-btn msf-btn--next"
                  style={{ margin: "0 auto" }}
                  onClick={() => fetchFilteredOpportunities(true)}
                >
                  Show All Opportunities
                </button> */}
              </div>
            ) : (
              (internshipOpportunities || []).map((op) => {
                const selected = selectedProposals.find(
                  (p) => p.internship_request_id === op.id,
                );
                return (
                  <div
                    key={op.id}
                    className={`msf-opportunity-card ${selected ? "msf-opportunity-card--selected" : ""}`}
                    onClick={() => handleProposalSelection(op)}
                  >
                    {selected && (
                      <div className="msf-opportunity-card__priority-badge">
                        #{selected.preference_order}
                      </div>
                    )}
                    <div className="msf-opportunity-card__header">
                      <div className="msf-opportunity-card__discipline">
                        {op.internship_discipline}
                      </div>
                      {selected && (
                        <div className="msf-opportunity-card__check">✓</div>
                      )}
                    </div>
                    <div className="msf-opportunity-card__body">
                      {op.brief_description && (
                        <p className="msf-opportunity-card__desc">
                          {op.brief_description}
                        </p>
                      )}
                      <div className="msf-opportunity-card__chips">
                        <span className="msf-info-chip msf-info-chip--blue">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                          {op.number_of_students} Students
                        </span>
                        {op.grade_criteria && (
                          <span className="msf-info-chip msf-info-chip--amber">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                            {op.grade_criteria}
                          </span>
                        )}
                      </div>
                      <div className="msf-opportunity-card__dates">
                        <div className="msf-date-range">
                          <span className="msf-date-range__icon">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect
                                x="3"
                                y="4"
                                width="18"
                                height="18"
                                rx="2"
                                ry="2"
                              />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                          </span>
                          <span>
                            {moment(op.preferred_start_date).format(
                              "DD MMM YYYY",
                            )}
                          </span>
                          <span className="msf-date-range__sep">→</span>
                          <span>
                            {moment(op.preferred_end_date).format(
                              "DD MMM YYYY",
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="msf-opportunity-card__footer">
                      <button
                        type="button"
                        className={`msf-select-btn ${selected ? "msf-select-btn--remove" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProposalSelection(op);
                        }}
                      >
                        {selected ? "✕ Remove" : "+ Select"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="msf-nav-buttons">
            <button
              type="button"
              className="msf-btn msf-btn--prev"
              onClick={goPrev}
            >
              <span className="msf-btn-arrow">←</span> Previous
            </button>
            <button
              type="button"
              className="msf-btn msf-btn--next"
              onClick={goNext}
            >
              Next: Preview <span className="msf-btn-arrow">→</span>
            </button>
          </div>
        </div>
      )}

      {/* ══════════════ PAGE 3 — PREVIEW ══════════════ */}
      {formPage === 3 && (
        <View
          {...props}
          basicData={{
            ...formData,
            is_kerala_native: isKeralaNative,
            course_completion_status: courseCompletionStatus,
            languages: (props.languageData || [])
              .filter((l) => l.checked)
              .map((l) => ({ language: l.language }))
              .concat(
                (formData.otherLanguages || [])
                  .filter((l) => l.language)
                  .map((l) => ({ language: l.language })),
              ),
            proposals: selectedProposals,
          }}
          languageData={props.languageData}
          qualificationsData={qualificationsData}
          internshipOpportunities={internshipOpportunities}
          action={(id) => {
            if (id === 1) {
              // Go back to internship selection
              setFormPage(2);
              window.scrollTo(0, 0);
            } else if (id === 2) {
              // Register
              handleFinalSubmit();
            }
          }}
        />
      )}

      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />
    </div>
  );
}
